import React, { useEffect, useLayoutEffect } from "react";
import { NetworkStatus } from "apollo-client";
import { Code } from "react-content-loader";
import { RouteComponentProps, withRouter } from "react-router";
import { Route } from "react-router-dom";
import { PanelCard } from "@sonder/ui/source/styled/fragments/PanelCard";
import { PanelContent } from "@sonder/ui/source/styled/layouts/PanelContent";
import { PanelLayout } from "@sonder/ui/source/styled/layouts/PanelLayout";
import { useSegment } from "analytics/segment";
import { useLayout } from "core/components/LayoutProvider";
import { THIRTY_SECONDS } from "core/constants";
import { usePageVisibilityPolling } from "core/hooks/usePageVisibilityPolling";
import { useRefetchableGetTaskQuery } from "core/queries/GetTask";
import { Activity } from "tasks/components/TaskPanel/Activity";
import { Details } from "tasks/components/TaskPanel/Details";
import { Guest } from "tasks/components/TaskPanel/Guest";
import { Header, HeaderLoading } from "tasks/components/TaskPanel/Header";
import { HeaderContentLoader } from "tasks/components/TaskPanel/Header.loader";
import { Nav, NavLoading } from "tasks/components/TaskPanel/Nav";
import { Unit } from "tasks/components/TaskPanel/Unit";

interface UseTaskQueryOptions {
  pollInterval?: number;
}

// Fetch task data
const useTask = (taskId: string, options: UseTaskQueryOptions = {}) => {
  const {
    data,
    error,
    loading,
    refetch,
    startPolling,
    stopPolling,
    networkStatus,
  } = useRefetchableGetTaskQuery({
    ...options,
    variables: { taskId },
  });
  const task = (data && data.task) || null;
  if (options.pollInterval) {
    usePageVisibilityPolling({
      pollInterval: options.pollInterval,
      refetch,
      startPolling,
      stopPolling,
    });
  }
  const isRefetching = networkStatus === NetworkStatus.refetch;
  const isPolling = networkStatus === NetworkStatus.poll;
  return { task, error, loading: loading && !isRefetching && !isPolling };
};

// Manage sidepanel collapsed state
const useSidepanel = () => {
  const { sidepanel, setSidepanel } = useLayout();
  useLayoutEffect(() => {
    // Expand sidepanel when first rendered
    if (sidepanel === "collapsed") {
      setSidepanel("expanded");
    }
    return () => {
      if (sidepanel === "expanded") {
        setSidepanel("collapsed");
      }
    };
  }, [sidepanel]);

  return { sidepanel, setSidepanel };
};

// Track users switching between different tasks
const useSegmentView = (taskId: string) => {
  const { track } = useSegment();
  useEffect(() => {
    track({ eventName: "task_panel", action: "view", props: { taskId } });
  }, [taskId]);
};

interface TaskPanelProps extends RouteComponentProps<{ taskId: string }> {
  onClose?(event: React.MouseEvent): void;
  pollInterval?: number;
}

export const TaskPanelSimple = ({
  match,
  onClose,
  pollInterval = THIRTY_SECONDS,
}: TaskPanelProps) => {
  const taskId = match.params.taskId;
  useSegmentView(taskId);
  const { task, error, loading } = useTask(taskId, {
    pollInterval,
  });

  const { sidepanel } = useSidepanel();

  if (sidepanel === "collapsed") return null;

  if (!task || loading || error) {
    return (
      <PanelLayout>
        <NavLoading message={error ? "Error" : ""} onClose={onClose} />
        <HeaderLoading message={error ? "Error" : <HeaderContentLoader />} />
        <PanelContent>
          <PanelCard>
            <Code />
          </PanelCard>
        </PanelContent>
      </PanelLayout>
    );
  }

  return (
    <PanelLayout>
      <Nav task={task} onClose={onClose} />
      <Header task={task} />
      <Route exact path={match.path} render={() => <Details task={task} />} />
      <Route
        exact
        path={`${match.path}/activity`}
        render={() => <Activity task={task} />}
      />
      <Route
        exact
        path={`${match.path}/guest`}
        render={() => <Guest task={task} />}
      />
      <Route
        exact
        path={`${match.path}/unit`}
        render={() => <Unit task={task} />}
      />
    </PanelLayout>
  );
};

export const TaskPanel = withRouter(TaskPanelSimple);
